import { useState } from 'react';
import clsx from 'clsx';
import { useAuth } from '@/hooks/use-auth';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { AuthLogo } from '@/components/layout/AuthLogo';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { SupportLink } from '@/components/ui';
import { posthog } from '../lib/posthog';

interface LoginSearchShape {
  /**
   * auth-flow-hardening Inc-1 (2026-05-20): when the user lands here after
   * a successful `/reset-password` submit, the reset page redirects with
   * `?reset=success` so we surface a banner instructing them to sign in
   * with the new password. Optional + best-effort — pure UX hint, no
   * behavioral coupling.
   */
  reset?: 'success';
  /**
   * Post-auth return path. Set by the public deal-link header CTA
   * (`authUrlWithShare('/login', shareId)` → `?next=%2Fdeal%2F<id>&share=<id>`)
   * and by the `/auth/callback` expired-share fallback. Honored on a
   * successful password sign-in so a recipient lands back on the deal they
   * came from instead of the generic `/cockpit`. Internal-only (see
   * `safeNextPath`) to avoid an open-redirect.
   */
  next?: string;
  /**
   * Deal-share id carried by the public deal-link CTAs
   * (`authUrlWithShare('/login', shareId)` → `?next=…&share=<id>`). When
   * present on a successful sign-in we route THROUGH `/auth/callback?share=<id>`
   * — that bridge runs the server-side `linkSharesForUser` hook (flips the
   * pending share to `active` + stamps `recipient_user_id`), which is what
   * makes the deal page resolve the caller as `internal-recipient` and bounce
   * them to their inbox. Routing straight to `next` (`/deal/<id>`) would skip
   * the link step and leave the recipient as a generic `external-authed`
   * viewer stuck on the public chrome.
   */
  share?: string;
  /**
   * Set by the admin-invite NOTICE email's CTA (`/login?invited=1`). Invited
   * users are provisioned CONFIRMED with NO password, so the login page is
   * tuned to Email-Code-only: password mode + the toggle + "Forgot password?"
   * are hidden, and Email Code is pre-selected. Cosmetic-only — any value is
   * truthy; the actual auth is the standard email-code flow.
   */
  invited?: string;
}

/**
 * Open-redirect guard: only honor a `next` that is a same-origin absolute
 * path (`/foo`). Reject protocol-relative (`//evil.com`), absolute URLs, and
 * anything that doesn't start with a single `/`. Returns the safe path or
 * `null` (caller falls back to `/cockpit`).
 */
function safeNextPath(next: string | undefined): string | null {
  if (!next) return null;
  if (!next.startsWith('/')) return null;
  if (next.startsWith('//')) return null;
  return next;
}

export function LoginPage() {
  const { signIn, sendEmailOtp, verifyEmailOtp } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as LoginSearchShape;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Invited users (from the invite notice `/login?invited=1`) have no password
  // yet → tune the page to Email-Code-only.
  const invitedMode = !!search.invited;
  const [mode, setMode] = useState<'password' | 'otp'>(
    invitedMode ? 'otp' : 'password'
  );
  // Email-code (OTP) sign-in: once the code email is dispatched we swap the
  // form for a 6-digit code-entry view (no magic link — see `sendEmailOtp`).
  const [otpSent, setOtpSent] = useState(false);
  // Persists across this mount only — we don't echo it back to the URL
  // (the cosmetic banner shouldn't survive a refresh).
  const [resetSuccessShown] = useState(search.reset === 'success');

  // Shared post-authentication routing for BOTH password + email-code sign-in.
  // Share-carrying sign-in → route through the `/auth/callback` bridge so the
  // pending share links to this account before we land on the deal.
  // `window.location.assign` (full-page nav) matches the existing bridge entry
  // convention — `/auth/callback` reads `?share=` via its own validateSearch +
  // waits for the just-established session.
  const routeAfterAuth = () => {
    if (search.share) {
      window.location.assign(
        `/auth/callback?share=${encodeURIComponent(search.share)}`
      );
      return;
    }
    const next = safeNextPath(search.next);
    // Full-page nav (NOT SPA `navigate`) so the app re-bootstraps with the
    // just-persisted session — `AuthProvider` reads it from localStorage and
    // populates `user` BEFORE `__root.tsx`'s cascade runs. An SPA `navigate`
    // here races the async `onAuthStateChange` update: the cascade can fire
    // with `user` still null → bounce to `/login` (the post-verifyOtp flash
    // caught in the 2026-05-29 auth walkthrough, S1/S2). The cascade still
    // gates the final destination (pending → under-review, no-balance →
    // checkout, FP → cockpit).
    window.location.assign(next ?? '/cockpit');
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error: err } = await signIn(email, password);
    setLoading(false);
    if (err) {
      setError(err.message);
      return;
    }
    routeAfterAuth();
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error: err } = await sendEmailOtp(email);
    setLoading(false);
    if (err) {
      setError(err.message);
      return;
    }
    // Email-code dispatched successfully (action-driven; no StrictMode guard).
    posthog.capture('email_otp_sent', {});
    setCode('');
    setOtpSent(true);
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error: err } = await verifyEmailOtp(email, code.trim());
    setLoading(false);
    if (err) {
      setError(err.message);
      return;
    }
    routeAfterAuth();
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-void">
      <div className="w-full max-w-sm px-8 py-10">
        <AuthLogo />

        {otpSent ? (
          <form
            onSubmit={handleVerifyOtp}
            className="flex flex-col items-center gap-4"
          >
            <div className="w-12 h-12 rounded-full bg-accent-dim border border-accent/20 flex items-center justify-center">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--color-accent)"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="M22 7l-8.97 5.7a1.94 1.94 0 01-2.06 0L2 7" />
              </svg>
            </div>
            <div className="text-center">
              <p className="text-base text-text-bright font-body font-medium mb-2">
                Enter your code
              </p>
              <p className="text-sm text-text-muted font-mono leading-relaxed">
                We sent a 6-digit code to{' '}
                <span className="text-text-bright">{email}</span>
              </p>
            </div>

            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-label="Verification code"
              value={code}
              onChange={(e) =>
                setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              placeholder="000000"
              required
              autoFocus
              className="w-full px-4 py-3.5 bg-input-bg border border-input-border rounded-[3px] text-text-bright font-mono text-xl text-center tracking-[0.5em] outline-none focus:border-accent/30 transition-colors"
            />

            {error && (
              <div className="w-full px-3 py-2.5 bg-danger/[0.06] border border-danger/13 rounded-[3px] flex flex-col gap-2">
                <p className="text-danger font-mono text-sm">{error}</p>
                <SupportLink variant="inline" subject="Sign-in code error" />
              </div>
            )}

            <button
              type="submit"
              disabled={loading || code.length < 6}
              className="w-full py-3.5 mt-1 bg-accent-dim border border-accent/15 rounded-[3px] text-accent font-mono text-sm font-semibold tracking-[0.5px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed animate-cta-idle hover:bg-accent/13 hover:border-accent/27 transition-all duration-300"
            >
              {loading ? 'Verifying...' : 'Verify & Sign In'}
            </button>

            <button
              type="button"
              onClick={() => {
                setOtpSent(false);
                setCode('');
                setError(null);
              }}
              className="mt-1 text-xs text-text-dim font-mono cursor-pointer bg-transparent border-none hover:text-text-muted transition-colors"
            >
              &larr; Use a different email
            </button>
          </form>
        ) : (
          <>
            <form
              onSubmit={
                mode === 'password' ? handlePasswordSubmit : handleSendOtp
              }
              className="flex flex-col gap-4"
            >
              <div>
                <label className="block text-xs text-text-muted font-mono mb-1.5 tracking-[0.5px]">
                  EMAIL
                </label>
                <input
                  type="email"
                  autoComplete="email"
                  aria-label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  className="w-full px-4 py-3.5 bg-input-bg border border-input-border rounded-[3px] text-text-bright font-mono text-md outline-none focus:border-accent/30 transition-colors"
                />
              </div>

              {/* Invited users: Email-Code-only, no password toggle. */}
              {invitedMode && (
                <p className="text-xs text-text-muted font-mono leading-relaxed text-center">
                  Welcome — sign in with your email to finish setting up your
                  account.
                </p>
              )}

              {/* Mode toggle — hidden for invited users (no password yet). */}
              <div className={clsx('flex gap-2', invitedMode && 'hidden')}>
                <button
                  type="button"
                  onClick={() => {
                    setMode('password');
                    setError(null);
                  }}
                  className={clsx(
                    'flex-1 py-1.5 text-xs font-mono rounded-[2px] border-none cursor-pointer transition-colors',
                    mode === 'password'
                      ? 'text-accent bg-accent/8'
                      : 'text-text-dim bg-transparent hover:text-text-muted'
                  )}
                >
                  Password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMode('otp');
                    setError(null);
                  }}
                  className={clsx(
                    'flex-1 py-1.5 text-xs font-mono rounded-[2px] border-none cursor-pointer transition-colors',
                    mode === 'otp'
                      ? 'text-accent bg-accent/8'
                      : 'text-text-dim bg-transparent hover:text-text-muted'
                  )}
                >
                  Email Code
                </button>
              </div>

              {mode === 'password' && (
                <div>
                  <label className="block text-xs text-text-muted font-mono mb-1.5 tracking-[0.5px]">
                    PASSWORD
                  </label>
                  <PasswordInput
                    autoComplete="current-password"
                    aria-label="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                </div>
              )}

              {resetSuccessShown && !error && (
                <div className="px-3 py-2.5 bg-accent-dim border border-accent/15 rounded-[3px]">
                  <p className="text-accent font-mono text-sm">
                    Password updated. Sign in with your new password to
                    continue.
                  </p>
                </div>
              )}

              {error && (
                <div className="px-3 py-2.5 bg-danger/[0.06] border border-danger/13 rounded-[3px] flex flex-col gap-2">
                  <p className="text-danger font-mono text-sm">{error}</p>
                  <SupportLink variant="inline" subject="Login error" />
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 mt-1 bg-accent-dim border border-accent/15 rounded-[3px] text-accent font-mono text-sm font-semibold tracking-[0.5px] cursor-pointer disabled:opacity-50 animate-cta-idle hover:bg-accent/13 hover:border-accent/27 transition-all duration-300"
              >
                {loading
                  ? mode === 'password'
                    ? 'Signing in...'
                    : 'Sending...'
                  : mode === 'password'
                  ? 'Sign In'
                  : 'Send Code'}
              </button>
            </form>

            <div className="mt-6 flex flex-col items-center gap-3">
              {mode === 'password' && (
                <button
                  onClick={() => navigate({ to: '/forgot-password' })}
                  className="text-xs text-text-dim font-mono cursor-pointer bg-transparent border-none hover:text-text-muted transition-colors"
                >
                  Forgot password?
                </button>
              )}
              <div className="w-full h-px bg-panel-border" />
              <div className="text-xs text-text-dim font-mono">
                Don&apos;t have an account?{' '}
                <button
                  onClick={() => navigate({ to: '/signup' })}
                  className="text-accent font-semibold cursor-pointer bg-transparent border-none hover:underline"
                >
                  Sign up
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
